import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllAppointments, updateAppointment, deleteAppointment, createAppointment } from '../../actions/appointmentActions';
import { getAllPatientProfiles, setPatientProfile } from '../../actions/patientProfileActions';
import { getAllUsers } from '../../actions/loginAction';
import { getScheduleData } from '../../actions/scheduleActions';
import { imgUrl } from '../../actions/types';
import '../../scss/style.scss';
import SelfAppointmentTab from './SelfAppointmentTab';
import { formatCurrency } from '../utils/currencyHelper';
import { sendConfirmationSms } from '../../actions/smsActions';
import { updateSubscriptionStatus } from 'src/actions/loginAction';
import { baseURL } from 'src/actions/types';
import { updateUser } from 'src/actions/loginAction';
// --- Filters Component ---
const AppointmentFilters = ({ 
  filters, 
  onFilterChange, 
  onSortChange, 
  onResetFilters,
  totalAppointments,
  filteredCount 
}) => {
  const statusOptions = ['All', 'Pending', 'Confirm', 'Cancel', 'Complete'];
  const typeOptions = ['All', 'direct', 'online'];
  const bookedByOptions = ['All', 'Patient', 'Doctor', 'Admin'];
  const sortOptions = [
    { value: 'id-desc', label: 'Appointment ID (Newest First)' },
    { value: 'id-asc', label: 'Appointment ID (Oldest First)' },
    { value: 'date-desc', label: 'Appointment Date (Newest First)' },
    { value: 'date-asc', label: 'Appointment Date (Oldest First)' },
    { value: 'name-asc', label: 'Patient Name (A-Z)' },
    { value: 'name-desc', label: 'Patient Name (Z-A)' }
  ];

  return (
    <div className="card mb-3">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Filters & Search</h5>
        <button 
          className=" btn btn-secondary rounded-pill btn-sm "
          onClick={onResetFilters}
        >
          Reset Filters
        </button>
      </div>
      <div className="card-body">
        <div className="row p-3">
          {/* Search by Name/ID */}
          <div className="col-md-3 mb-3">
            <label className="form-label small fw-bold">Search Patient/ID</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search by name or appointment ID..."
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
            />
          </div>

         

          {/* Status Filter */}
          <div className="col-md-2 mb-3">
            <label className="form-label small fw-bold">Status</label>
            <select
              className="form-control form-control-sm"
              value={filters.status}
              onChange={(e) => onFilterChange('status', e.target.value)}
            >
              {statusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div className="col-md-2 mb-3">
            <label className="form-label small fw-bold">Type</label>
            <select
              className="form-control form-control-sm"
              value={filters.type}
              onChange={(e) => onFilterChange('type', e.target.value)}
            >
              {typeOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Booked By Filter */}
          <div className="col-md-2 mb-3">
            <label className="form-label small fw-bold">Booked By</label>
            <select
              className="form-control form-control-sm"
              value={filters.bookedBy}
              onChange={(e) => onFilterChange('bookedBy', e.target.value)}
            >
              {bookedByOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Sort Options */}
          <div className="col-md-3 mb-3">
            <label className="form-label small fw-bold">Sort By</label>
            <select
              className="form-control form-control-sm"
              value={filters.sortBy}
              onChange={(e) => onSortChange(e.target.value)}
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Results Count */}
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              {filteredCount < totalAppointments && (
                <small className="text-info">
                  <i className="fas fa-filter me-1"></i>
                  Filters active
                </small>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Updated Appointment Table Component with Filtering and Sorting ---
const AppointmentTable = ({ 
  appointments, 
  patientMap, 
  onUpdateStatus, 
  isUpdating, 
  onViewDetails,
   selectedCurrency ,
  filters 
}) => {
  // Apply filters and sorting
  const filteredAndSortedAppointments = React.useMemo(() => {
    let filtered = [...appointments];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(apt => {
        const profile = patientMap[apt.PatientID] || {};
        const patientFullName = profile.firstName && profile.lastName 
          ? `${profile.firstName} ${profile.lastName}`.toLowerCase()
          : (apt.patient_name || '').toLowerCase();
        
        return (
          patientFullName.includes(searchTerm) ||
          apt.appointmentId.toString().includes(searchTerm) ||
          (profile.email || '').toLowerCase().includes(searchTerm)
        );
      });
    }

    // Date filter
    if (filters.date) {
      filtered = filtered.filter(apt => 
        apt.appointmentDate === filters.date
      );
    }

    // Status filter
    if (filters.status !== 'All') {
      filtered = filtered.filter(apt => apt.status === filters.status);
    }

    // Type filter
    if (filters.type !== 'All') {
      filtered = filtered.filter(apt => apt.type === filters.type);
    }

    // Booked By filter
    if (filters.bookedBy !== 'All') {
      filtered = filtered.filter(apt => apt.appointmentBookedBy === filters.bookedBy);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'id-asc':
          return a.appointmentId - b.appointmentId;
        case 'id-desc':
          return b.appointmentId - a.appointmentId;
        case 'date-asc':
          return new Date(a.appointmentDate) - new Date(b.appointmentDate);
        case 'date-desc':
          return new Date(b.appointmentDate) - new Date(a.appointmentDate);
        case 'name-asc': {
          const profileA = patientMap[a.PatientID] || {};
          const profileB = patientMap[b.PatientID] || {};
          const nameA = `${profileA.firstName || ''} ${profileA.lastName || ''}`.toLowerCase();
          const nameB = `${profileB.firstName || ''} ${profileB.lastName || ''}`.toLowerCase();
          return nameA.localeCompare(nameB);
        }
        case 'name-desc': {
          const profileA = patientMap[a.PatientID] || {};
          const profileB = patientMap[b.PatientID] || {};
          const nameA = `${profileA.firstName || ''} ${profileA.lastName || ''}`.toLowerCase();
          const nameB = `${profileB.firstName || ''} ${profileB.lastName || ''}`.toLowerCase();
          return nameB.localeCompare(nameA);
        }
        default:
          return b.appointmentId - a.appointmentId; // Default: newest first
      }
    });

    return filtered;
  }, [appointments, patientMap, filters]);

  return (
    <div className="table-responsive">
      <table className="table table-hover table-center mb-0">
        <thead>
          <tr>
            <th>APPT ID</th>
            <th>Patient Info</th>
            <th>Booked By</th>
            <th>Booked For</th>
            <th>Appointment Date</th>
            <th>Time</th>
            <th>Type</th>
            <th>Paid Amount</th>
            <th>Status</th>
            <th className="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedAppointments.length === 0 ? (
            <tr>
              <td colSpan="10" className="text-center text-muted py-4">
                <i className="fas fa-search fa-2x mb-2 d-block"></i>
                No appointments found matching your filters.
              </td>
            </tr>
          ) : (
            filteredAndSortedAppointments.map((apt) => {
              const profile = patientMap[apt.PatientID] || {};
              const patientFullName = profile.firstName && profile.lastName 
                ? `${profile.firstName} ${profile.lastName}`
                : apt.patient_name || 'N/A';
              const patientEmail = profile.email || 'N/A';
              const patientPhoto = profile.uploadPhoto ? `${imgUrl}${profile.uploadPhoto}` : 'assets/img/patients/patient.jpg';

              // Booked By - who made the booking
              const bookedBy = apt.appointmentBookedBy || 'Unknown';
              const bookedByDisplay = bookedBy === 'Doctor' ? 'Doctor' : 
                                    bookedBy === 'Patient' ? 'Patient' : 
                                    bookedBy === 'Admin' ? 'Admin' : 
                                    bookedBy;

              // Booked For - check if patient booked for themselves or someone else
              const isSelfBooking = bookedBy === 'Patient' && 
                                  patientFullName.toLowerCase() === (apt.patient_name || '').toLowerCase();
              
              const bookedForDisplay = isSelfBooking ? 'Self' : apt.patient_name;

              return (
                <tr key={apt.appointmentId}>
                  <td>
                    <span className="fw-bold text-info">#{apt.appointmentId}</span>
                  </td>
                  <td>
                    <Link to={`/patient-profile/${apt.PatientID}`} className="table-avatar d-flex align-items-center">
                      <div className="avatar avatar-sm mr-2">
                        <img className="avatar-img rounded-circle" src={patientPhoto} alt="Patient" />
                      </div>
                      <div>
                        <h6 className='text-dark mb-1'>{patientFullName}</h6>
                        <small className="text-muted">{patientEmail}</small>
                      </div>
                    </Link>
                  </td>
                  <td>
                    <span className={
                      bookedBy === 'Doctor' ? 'badge badge-success' :
                      bookedBy === 'Patient' ? 'badge badge-info' :
                      bookedBy === 'Admin' ? 'badge badge-warning' :
                      'badge badge-secondary'
                    }>
                      {bookedByDisplay}
                    </span>
                  </td>
                  <td>
                    <span className={
                      isSelfBooking ? 'badge badge-success' : 'badge badge-info'
                    }>
                      {bookedForDisplay}
                    </span>
                  </td>
                  <td>
                    <span className="text-nowrap">
                      {new Date(apt.appointmentDate).toLocaleDateString()}
                    </span>
                  </td>
                  <td>{apt.appointmentTime}</td>
                  <td>
                    <span className={
                      apt.type === 'direct' ? 'badge badge-success' :
                      apt.type === 'online' ? 'badge badge-info' :
                      'badge badge-secondary'
                    }>
                      {apt.type || 'N/A'}
                    </span>
                  </td>
                  <td>
  {apt.paidAmount ? (
    <span className="fw-bold text-success">
      {formatCurrency(apt.paidAmount, selectedCurrency)} 
    </span>
  ) : (
    'N/A'
  )}
</td>
                  <td>
                    <span className={
                      apt.status === 'Confirm' ? 'confirm-label' :
                      apt.status === 'Cancel' ? 'cancel-label' :
                      apt.status === 'Complete' ? 'complete-label' :
                      apt.status === 'Pending' ? 'pending-label' :
                      apt.status === 'Processing' ? 'processing-label' :
                      apt.status === 'Paid' ? 'paid-label' :
                      apt.status === 'Failed' ? 'failed-label' :
                      apt.status === 'Active' ? 'active-label' :
                      apt.status === 'Draft' ? 'draft-label' :
                      apt.status === 'Online' ? 'online-label' :
                      'pending-label'
                    }>
                      {apt.status}
                    </span>
                  </td>
                  <td className="text-center">
                  <div className="table-action">
                    <button
                      className="i-btn-view"
                      onClick={() => onViewDetails({ ...apt, patient: profile })}
                      aria-label={`View details for appointment ${apt.appointmentId}`}
                      title='View Appoinment Details'
                    >
                    
                    </button>
                    <button
                      className= "i-btn-confirm"
                      onClick={() => onUpdateStatus(apt.appointmentId, 'Confirm')}
                      aria-label={`Confirm appointment ${apt.appointmentId}`}
                       title='Confirm Appoinment '
                    >
                      {isUpdating && apt.status !== 'Confirm' ? '' : (<> </>)}
                    </button>
                    <button
                      className="i-btn-cancel"
                      onClick={() => onUpdateStatus(apt.appointmentId, 'Cancel')}
                      aria-label={`Cancel appointment ${apt.appointmentId}`}
                      title='Cancel Appoinment '
                    >
                      {isUpdating && apt.status !== 'Cancel' ? '' : (<> </>)}
                    </button>
                    <button
                      className= "i-btn-complete"
                      onClick={() => onUpdateStatus(apt.appointmentId, 'Complete')}
                      aria-label={`Complete appointment ${apt.appointmentId}`}
                      title='Complete Appoinment '
                    >
                      {isUpdating && apt.status !== 'Complete' ? '' : (<> </>)}
                    </button>
                  </div>
                </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

// --- Appointment Details Modal (Keep as before) ---
const AppointmentDetailsModal = ({ show, onClose, appointment ,selectedCurrency}) => {
  if (!show || !appointment) return null;

  return (
    <div className={`modal fade custom-modal ${show ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-modal="true">
      <div className="modal-dialog modal-dialog-centered" role="document">
        {/* <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Appointment Details</h5>
            <button type="button" className="close" onClick={onClose} aria-label="Close">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div className="modal-body">
            <ul className="info-details list-unstyled">
              <li><span className="title">Appointment Id:</span><span className="text"># {appointment.appointmentId}</span></li>
              <li><span className="title">Patient Name:</span><span className="text">{appointment.patient?.firstName} {appointment.patient?.lastName}</span></li>
              <li><span className="title">Patient ID:</span><span className="text">{appointment.PatientID}</span></li>
              <li><span className="title">Booked By:</span><span className="text">{appointment.bookedBy || appointment.appointmentBookedBy || 'N/A'}</span></li>
              <li><span className="title">Booked For:</span><span className="text">{appointment.bookedFor || 'N/A'}</span></li>
              <li><span className="title">Email:</span><span className="text">{appointment.patient?.email || 'N/A'}</span></li>
              <li><span className="title">Phone:</span><span className="text">{appointment.patient?.phoneNumber || 'N/A'}</span></li>
              <li><span className="title">Appointment Date:</span><span className="text">{new Date(appointment.appointmentDate).toLocaleDateString()}</span></li>
              <li><span className="title">Time:</span><span className="text">{appointment.appointmentTime}</span></li>
              <li><span className="title">Type:</span><span className="text">{appointment.type}</span></li>
              <li><span className="title">Status:</span><span className="text">{appointment.status}</span></li>
              <li><span className="title">Paid Amount:</span><span className="text">{appointment.paidAmount ? `${appointment.paidAmount}` : 'N/A'}</span></li>
              <li><span className="title">Booking Date:</span><span className="text">{new Date(appointment.bookingDate).toLocaleDateString()}</span></li>
              <li><span className="title">Hospital ID:</span><span className="text">{appointment.hospitalID}</span></li>
            </ul>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div> */}
        <div 
  className="modal show d-block" 
  style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
>
  <div className="modal-dialog modal-dialog-centered modal-lg">
    <div className="modal-content">
      {/* Header: Applied bg-info and white text style */}
      <div className="modal-header bg-info text-white no-print">
        <h5 className="modal-title">Appointment Details</h5>
        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={onClose}
          aria-label="Close"
        ></button>
      </div>

      <div className="modal-body">
        <div className="row">
          {/* Left Column */}
          <div className="col-md-6 border-end">
            <ul className="info-details list-unstyled">
              <li>
                <span className="title">Appointment Id:</span>
                <span className="text fw-bold"> # {appointment.appointmentId}</span>
              </li>
              <li>
                <span className="title">Patient Name:</span>
                <span className="text">{appointment.patient?.firstName} {appointment.patient?.lastName}</span>
              </li>
              <li>
                <span className="title">Patient ID:</span>
                <span className="text">{appointment.PatientID}</span>
              </li>
              <li>
                <span className="title">Email:</span>
                <span className="text">{appointment.patient?.email || 'N/A'}</span>
              </li>
              <li>
                <span className="title">Phone:</span>
                <span className="text">{appointment.patient?.phoneNumber || 'N/A'}</span>
              </li>
              <li>
  <span className="title">Paid Amount:</span>
  <span className="text">
    {appointment.paidAmount ? formatCurrency(appointment.paidAmount, selectedCurrency) : 'N/A'}
  </span>
</li>
              <li>
                <span className="title">Hospital ID:</span>
                <span className="text">{appointment.hospitalID}</span>
              </li>
            </ul>
          </div>

          {/* Right Column */}
          <div className="col-md-6">
            <ul className="info-details list-unstyled">
              <li>
                <span className="title">Status:</span>
                <span className={`badge rounded-pill ms-2 ${
                  appointment.status === "Confirmed" ? "bg-success" : 
                  appointment.status === "Complete" ? "bg-info" : 
                  appointment.status === "Canceled" ? "bg-danger" : "bg-warning"
                }`}>
                  {appointment.status}
                </span>
              </li>
              <li>
                <span className="title">Appointment Date:</span>
                <span className="text">{new Date(appointment.appointmentDate).toLocaleDateString()}</span>
              </li>
              <li>
                <span className="title">Time:</span>
                <span className="text">{appointment.appointmentTime}</span>
              </li>
              <li>
                <span className="title">Type:</span>
                <span className="text">{appointment.type}</span>
              </li>
              <li>
                <span className="title">Booked By:</span>
                <span className="text">{appointment.bookedBy || appointment.appointmentBookedBy || 'N/A'}</span>
              </li>
              <li>
                <span className="title">Booked For:</span>
                <span className="text">{appointment.bookedFor || 'N/A'}</span>
              </li>
              <li>
                <span className="title">Booking Date:</span>
                <span className="text">{new Date(appointment.bookingDate).toLocaleDateString()}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary rounded-pill" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  </div>
</div>
      </div>
    </div>
  );
};

const DoctorDashboardMain = ({
  appointment: allAppointments,
  profiles,
  templateSlots,
  daysOff,
  blockers,
  sendConfirmationSms,
  getAllAppointments,
  getAllPatientProfiles,
  updateAppointment,
  deleteAppointment,
  createAppointment,
   selectedCurrency, 
  setPatientProfile,
  getScheduleData,
}) => {
  const hospitalCode = parseInt(localStorage.getItem('hospitalCode'), 10);
  const [activeTab, setActiveTab] = useState('today');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [error, setError] = useState(null);
    const dispatch = useDispatch();
  // Filter states for each tab
  const [todayFilters, setTodayFilters] = useState({
    search: '',
    date: '',
    status: 'All',
    type: 'All',
    bookedBy: 'All',
    sortBy: 'id-desc'
  });

  const [upcomingFilters, setUpcomingFilters] = useState({
    search: '',
    date: '',
    status: 'All',
    type: 'All',
    bookedBy: 'All',
    sortBy: 'id-desc'
  });

  // Get current filters based on active tab
  const getCurrentFilters = () => {
    return activeTab === 'today' ? todayFilters : upcomingFilters;
  };

  const getCurrentAppointments = () => {
    return activeTab === 'today' ? todayAppointments : upcomingAppointments;
  };

  // Memoized fetch functions
  const fetchAllAppointments = useCallback(async () => {
    setLoadingAppointments(true);
    setError(null);
    try {
      await getAllAppointments(hospitalCode);
    } catch (err) {
      setError("Failed to load appointments.");
      console.error("Error fetching appointments:", err);
    } finally {
      setLoadingAppointments(false);
    }
  }, [getAllAppointments, hospitalCode]);

  const fetchAllPatientProfiles = useCallback(async () => {
    setLoadingProfiles(true);
    setError(null);
    try {
      await getAllPatientProfiles();
    } catch (err) {
      setError("Failed to load patient profiles.");
      console.error("Error fetching patient profiles:", err);
    } finally {
      setLoadingProfiles(false);
    }
  }, [getAllPatientProfiles]);

  const fetchScheduleData = useCallback(async () => {
    try {
      if (hospitalCode) {
        await getScheduleData({ hospitalID: hospitalCode });
      }
    } catch (err) {
      console.error("Error fetching schedule data:", err);
    }
  }, [hospitalCode, getScheduleData]);

  useEffect(() => {
    fetchAllAppointments();
    fetchAllPatientProfiles();
    fetchScheduleData();
  }, [fetchAllAppointments, fetchAllPatientProfiles, fetchScheduleData]);
  useEffect(() => {
        verifySubscriptionOnDashboard();
    }, []);

  const isToday = (dateStr) => new Date(dateStr).toDateString() === new Date().toDateString();
     const parseDate = (dateString) => {
        if (!dateString) return null;
        try {
            const parts = dateString.split('-');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);
                return new Date(year, month, day);
            }
        } catch (e) {}
        return null;
    };

    const checkActivePlan = (businessModules, hospitalID) => {
        if (!businessModules || businessModules.length === 0) return null;

        const today = new Date();
        const userModules = businessModules.filter(
            m => m.hospitalID === parseInt(hospitalID)
        );

        const activePlans = userModules.filter(plan => {
            if (!plan.businessType ||
                (plan.businessType !== 'subscription' && plan.businessType !== 'commission')) {
                return false;
            }
            if (plan.businessType === 'commission' && !plan.valid_To) return true;
            if (plan.valid_To) {
                const validToDate = parseDate(plan.valid_To);
                if (validToDate && validToDate > today) return true;
            }
            return false;
        });

        if (activePlans.length === 0) return null;
        return activePlans.sort((a, b) => b.id - a.id)[0];
    };

    const verifySubscriptionOnDashboard = async () => {
        try {
            const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
            const token = loginData.token;
            const hospitalID = loginData.hospitalID;

            if (!token || !hospitalID) return;

            console.log('[Dashboard] Verifying subscription...');

            // Step 1: Business modules fetch karo
            const response = await fetch(
                `${baseURL}/api/appointmentBusinessModules/getAll`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ hospitalID: parseInt(hospitalID) })
                }
            );

            const data = await response.json();
            const businessModules = (data?.success && Array.isArray(data.data))
                ? data.data : [];

            // Step 2: Active plan check karo
            const activePlan = checkActivePlan(businessModules, hospitalID);
            const subscriptionStatus = activePlan ? 'active' : 'inactive';
            const subscriptionType = activePlan ? activePlan.businessType : null;

            console.log('[Dashboard] Subscription result:', { subscriptionStatus, subscriptionType });

            // Step 3: Redux update karo
            dispatch(updateSubscriptionStatus(subscriptionStatus, subscriptionType));

            // Step 4: User table update karo
            const updatePayload = {
                ...loginData,
                subscription_status: subscriptionStatus,
                Subscription_type: subscriptionType,
                app_status: loginData.app_status || 'enabled',
            };
            delete updatePayload.token; // token mat bhejo

            await dispatch(updateUser(updatePayload));

            console.log('[Dashboard] Subscription verified and user updated.');

        } catch (error) {
            console.error('[Dashboard] Subscription check failed:', error);
        }
    };
  // Filter appointments relevant to this doctor
  const doctorAppointments = allAppointments.filter(
    (apt) => Number(apt.hospitalID) === hospitalCode
  );

  const todayAppointments = doctorAppointments.filter(
    (apt) => apt.appointmentDate && isToday(apt.appointmentDate)
  );

  const upcomingAppointments = doctorAppointments.filter(
    (apt) => apt.appointmentDate && new Date(apt.appointmentDate) > new Date() && !isToday(apt.appointmentDate)
  );

  const totalPatients = new Set(doctorAppointments.map((apt) => apt.PatientID)).size;

  // Create a map for quick patient profile lookup
  const patientMap = profiles.reduce((acc, profile) => {
    acc[profile.PatientID] = profile;
    return acc;
  }, {});

  // const handleUpdateStatus = async (id, status) => {
  //   setIsUpdating(true);
  //   try {
  //     await updateAppointment({ appointmentId: id, status });
  //     await fetchAllAppointments();
  //   } catch (err) {
  //     console.error("Error updating appointment status:", err);
  //   } finally {
  //     setIsUpdating(false);
  //   }
  // };
const handleUpdateStatus = async (id, status) => {
    setIsUpdating(true);
    try {
      // 1. Backend Status Update Call
      await updateAppointment({ appointmentId: id, status });
      
      // 2. Appointment aur Patient data nikalna
      const apt = allAppointments.find(a => a.appointmentId === id);
      
      if (apt) {
        const patientProfile = patientMap[apt.PatientID];
        const loginData = JSON.parse(localStorage.getItem('loginData') || '{}');
        const doctorFullName = ` ${loginData.first_name || ""} ${loginData.last_name || ""}`.trim();
        const patientFullName = `${patientProfile?.firstName || ""} ${patientProfile?.lastName || ""}`.trim();

        if (patientProfile?.phoneNumber) {
          
          // --- CASE 1: Status is 'Complete' (Purana Logic) ---
          if (status === 'Complete') {
            await sendConfirmationSms({ 
              hid: "2",
              recipientPhone: patientProfile.phoneNumber,
              eventId: "MEDISEVAS_APP_PATIENT_VISIT_COMPLETION",
              templateData: {
                doctorName: doctorFullName, 
              },
            });
          }

          // --- CASE 2: Status is 'Confirm' OR 'Cancel' (Naya Status Logic) ---
          else if (status === 'Confirm' || status === 'Cancel') {
            await sendConfirmationSms({
              hid: "2",
              recipientPhone: patientProfile.phoneNumber,
              eventId: "MEDISEVAS_APP_PATIENT_APPOINTMENT_STATUS",
              templateData: {
                patientName: patientFullName,
                doctorName: doctorFullName,
                details: status, // Yahan 'Confirm' ya 'Cancel' jayega
                url: "https://medisevas.com" // Template variable {{url}}
              },
            });
          }
        }
      }

      // 3. UI Refresh
      await fetchAllAppointments();
    } catch (err) {
      console.error("Error updating appointment status:", err);
    } finally {
      setIsUpdating(false);
    }
  };
  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setModalShow(true);
  };

  const handleCloseModal = () => {
    setModalShow(false);
    setSelectedAppointment(null);
  };

  const handleFilterChange = (filterType, value) => {
    const updateFunction = activeTab === 'today' ? setTodayFilters : setUpcomingFilters;
    updateFunction(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleSortChange = (sortValue) => {
    const updateFunction = activeTab === 'today' ? setTodayFilters : setUpcomingFilters;
    updateFunction(prev => ({
      ...prev,
      sortBy: sortValue
    }));
  };

  const handleResetFilters = () => {
    const resetFilters = {
      search: '',
      date: '',
      status: 'All',
      type: 'All',
      bookedBy: 'All',
      sortBy: 'id-desc'
    };
    
    if (activeTab === 'today') {
      setTodayFilters(resetFilters);
    } else {
      setUpcomingFilters(resetFilters);
    }
  };

  const overallLoading = loadingAppointments || loadingProfiles;

  if (overallLoading) {
    return (
      <div className="container mt-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4 alert alert-danger" role="alert">
        <h4 className="alert-heading">Error!</h4>
        <p>{error}</p>
        <hr />
        <p className="mb-0">Please try refreshing the page or contact support if the issue persists.</p>
      </div>
    );
  }

  return (
    <div className="">
      {/* <h4 className="mb-4">Doctor Dashboard Overview</h4> */}
       <div className="page-header filter-bar rounded ">
                <div className="row align-items-center">
                    <div className="col">
                        <h4 className="page-title">Doctor Dashboard</h4>
                        <ul className="breadcrumb">
                            <li className="breadcrumb-item"><Link to="/doctor/dashboard">Doctor Dashboard</Link></li>
                            <li className="breadcrumb-item active">Doctor Dashboard</li>
                        </ul>
                    </div>
                </div>
            </div>

      <div className="row">
        <div className="col-md-12">
          <div className="card dash-card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-12 col-lg-4">
                  <div className="dash-widget dct-border-rht">
                    <div className="circle-bar circle-bar1">
                      <div className="circle-graph1" data-percent="75">
                        <img src="assets/img/icon-01.png" className="img-fluid" alt="Total Patients" />
                      </div>
                    </div>
                    <div className="dash-widget-info">
                      <h6>Total Patients</h6>
                      <h3>{totalPatients}</h3>
                      <p className="text-muted">Till Today</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-12 col-lg-4">
                  <div className="dash-widget dct-border-rht">
                    <div className="circle-bar circle-bar2">
                      <div className="circle-graph2" data-percent="65">
                        <img src="assets/img/icon-02.png" className="img-fluid" alt="Today's Appointments" />
                      </div>
                    </div>
                    <div className="dash-widget-info">
                      <h6>Today's Appointments</h6>
                      <h3>{todayAppointments.length}</h3>
                      <p className="text-muted">{new Date().toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-12 col-lg-4">
                  <div className="dash-widget">
                    <div className="circle-bar circle-bar3">
                      <div className="circle-graph3" data-percent="50">
                        <img src="assets/img/icon-03.png" className="img-fluid" alt="Upcoming Appointments" />
                      </div>
                    </div>
                    <div className="dash-widget-info">
                      <h6>Upcoming Appointments</h6>
                      <h3>{upcomingAppointments.length}</h3>
                      <p className="text-muted">Future Appointments</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ul className="nav nav-tabs nav-tabs-solid nav-tabs-rounded mt-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'today' ? 'active' : ''}`}
            onClick={() => setActiveTab('today')}
            aria-controls="todayAppointmentsTab"
            aria-selected={activeTab === 'today'}
            role="tab"
          >
            Today's Appointments ({todayAppointments.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => setActiveTab('upcoming')}
            aria-controls="upcomingAppointmentsTab"
            aria-selected={activeTab === 'upcoming'}
            role="tab"
          >
            Upcoming Appointments ({upcomingAppointments.length})
          </button>
        </li>
        {/* <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'self' ? 'active' : ''}`}
            onClick={() => setActiveTab('self')}
            aria-controls="selfAppointmentTab"
            aria-selected={activeTab === 'self'}
            role="tab"
          >
            Self Appointment
          </button>
        </li> */}
      </ul>

      <div className="card card-table mt-3">
        <div className="card-body">
          <div className="tab-content px-3">
            {/* Today's Appointments Tab */}
            <div className={`tab-pane fade ${activeTab === 'today' ? 'show active' : ''}`} id="todayAppointmentsTab" role="tabpanel">
              <AppointmentFilters
                filters={todayFilters}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onResetFilters={handleResetFilters}
                totalAppointments={todayAppointments.length}
                filteredCount={todayAppointments.length} // You can implement filtered count calculation
              />
              <AppointmentTable
                appointments={todayAppointments}
                patientMap={patientMap}
                onUpdateStatus={handleUpdateStatus}
                isUpdating={isUpdating}
                onViewDetails={handleViewDetails}
                filters={todayFilters}
                 selectedCurrency={selectedCurrency}
              />
            </div>

            {/* Upcoming Appointments Tab */}
            <div className={`tab-pane fade ${activeTab === 'upcoming' ? 'show active' : ''}`} id="upcomingAppointmentsTab" role="tabpanel">
              <AppointmentFilters
                filters={upcomingFilters}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onResetFilters={handleResetFilters}
                totalAppointments={upcomingAppointments.length}
                filteredCount={upcomingAppointments.length} // You can implement filtered count calculation
              />
              <AppointmentTable
                appointments={upcomingAppointments}
                patientMap={patientMap}
                onUpdateStatus={handleUpdateStatus}
                isUpdating={isUpdating}
                onViewDetails={handleViewDetails}
                filters={upcomingFilters}
                 selectedCurrency={selectedCurrency}
              />
            </div>

            {/* Self Appointment Tab */}
            <div className={`tab-pane fade ${activeTab === 'self' ? 'show active' : ''}`} id="selfAppointmentTab" role="tabpanel">
              <SelfAppointmentTab
                hospitalCode={hospitalCode}
                templateSlots={templateSlots}
                daysOff={daysOff}
                blockers={blockers}
                bookedAppointments={allAppointments}
                profiles={profiles}
                setPatientProfile={setPatientProfile}
                createAppointment={createAppointment}
                getScheduleData={getScheduleData}
                getAllAppointments={getAllAppointments}
                isCreatingAppointment={isUpdating}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Appointment Details Modal */}
      <AppointmentDetailsModal
        show={modalShow}
        onClose={handleCloseModal}
        appointment={selectedAppointment}
        selectedCurrency={selectedCurrency} 
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  appointment: state.appointment?.all || [],
  profiles: state.patientProfile?.profiles || [],
  templateSlots: state.schedule?.templateSlots || [],
  daysOff: state.schedule?.daysOff || [],
  blockers: state.schedule?.blockers || [],
    selectedCurrency: state.currencyReducer.selectedCurrency,
});

export default connect(mapStateToProps, {
  getAllAppointments,
  getAllPatientProfiles,
  updateAppointment,
  deleteAppointment,
  createAppointment,
  setPatientProfile,
  getScheduleData,
  sendConfirmationSms
})(DoctorDashboardMain);